import {
  buildNormalizedDay,
  buildNormalizedPilotBlueprint,
  localizedGenderNode,
} from "./referenceNormalizerPilotUtils";

/** Required substring for scripts/validate_dna_blueprints.mjs (runtime writing steps). */
export const DNA_VALIDATE_WRITING_PHRASE = "אני שואל/ת את עצמי בשקט";

const days = [
  {
    dayNumber: 1,
    emotionalStage: "pause",
    title: "Noticing jealousy without panicking",
    opening: localizedGenderNode(
      [
      "Today I don’t try to be “over it.” I notice the jealousy — and I stay with myself.",
      "I pause with myself."
    ],
      [
      "Today I don’t try to be “over it.” I notice the jealousy — and I stay with myself.",
      "I pause with myself."
    ]
    ),
    mindfulness: localizedGenderNode(
      [
      "One slow breath.",
      "I say quietly inside: “This is pain and jealousy. They are not a command.”"
    ],
      [
      "One slow breath.",
      "I say quietly inside: “This is pain and jealousy. They are not a command.”"
    ]
    ),
    prompts: localizedGenderNode(
      [
      "Writing — for me\nI ask myself:\nWhat do I feel right now when the image/thought of my ex with someone else comes up? (one or two words)",
      "I ask myself:\nWhere in my body do I feel this? (chest/stomach/throat/head)",
      "I ask myself:\nWhat is the thing that burns the most inside this? (being replaced? rejection? comparison? fear?)"
    ],
      [
      "Writing — for me\nI ask myself:\nWhat do I feel right now when the image/thought of my ex with someone else comes up? (one or two words)",
      "I ask myself:\nWhere in my body do I feel this? (chest/stomach/throat/head)",
      "I ask myself:\nWhat is the thing that burns the most inside this? (being replaced? rejection? comparison? fear?)"
    ]
    ),
    closing: localizedGenderNode(
      [
      "Closing",
      "Today I don’t argue with the emotion. I see it and breathe beside it."
    ],
      [
      "Closing",
      "Today I don’t argue with the emotion. I see it and breathe beside it."
    ]
    ),
    action: localizedGenderNode(
      "When a trigger appears — I do a “30-second pause”: one breath + one sentence inside: “This is a wave. It passes.”",
      "When a trigger appears — I do a “30-second pause”: one breath + one sentence inside: “This is a wave. It passes.”"
    ),
  },
  {
    dayNumber: 2,
    emotionalStage: "pain",
    title: "Story vs. fact (stop living in a movie)",
    opening: localizedGenderNode(
      [
      "Today I separate what I know from what my mind paints.",
      "I pause with myself."
    ],
      [
      "Today I separate what I know from what my mind paints.",
      "I pause with myself."
    ]
    ),
    mindfulness: localizedGenderNode(
      [
      "One breath. “I don’t have to believe every image.”"
    ],
      [
      "One breath. “I don’t have to believe every image.”"
    ]
    ),
    prompts: localizedGenderNode(
      [
      "Writing\nWhat is the bare fact I know? (2 lines, no interpretation)",
      "What movie does my mind build around it? (what do I imagine it means?)",
      "What does it do to my body when I believe that movie?",
      "What one gentle truth can I choose today instead of the movie? (one sentence)"
    ],
      [
      "Writing\nWhat is the bare fact I know? (2 lines, no interpretation)",
      "What movie does my mind build around it? (what do I imagine it means?)",
      "What does it do to my body when I believe that movie?",
      "What one gentle truth can I choose today instead of the movie? (one sentence)"
    ]
    ),
    closing: localizedGenderNode(
      [
      "Closing",
      "Not every thought is truth. Sometimes it’s just anxiety asking for control."
    ],
      [
      "Closing",
      "Not every thought is truth. Sometimes it’s just anxiety asking for control."
    ]
    ),
    action: localizedGenderNode(
      "Once today I say: “This is a story. I’m returning to the facts.” Then one breath.",
      "Once today I say: “This is a story. I’m returning to the facts.” Then one breath."
    ),
  },
  {
    dayNumber: 3,
    emotionalStage: "pain",
    title: "The urge to check/peek (90-second protocol)",
    opening: localizedGenderNode(
      [
      "Today I build a pause before action, so jealousy doesn’t run my life.",
      "I pause with myself."
    ],
      [
      "Today I build a pause before action, so jealousy doesn’t run my life.",
      "I pause with myself."
    ]
    ),
    mindfulness: localizedGenderNode(
      [
      "One breath. “I choose me first.”"
    ],
      [
      "One breath. “I choose me first.”"
    ]
    ),
    prompts: localizedGenderNode(
      [
      "Writing\nWhat urge comes up for me today? (to peek/check/search/message)",
      "What am I really trying to soothe through this urge? (uncertainty? pain? fear of abandonment? humiliation?)",
      "90-second protocol (from today onward)\n\n1. 3 slow breaths\n\n\n2. Notice the body: where the urge sits\n\n\n3. One sentence inside: “This is an urge. It will pass.”\nThen choose one small alternative.\n\n\n\nWhat one alternative do I choose today instead of checking?"
    ],
      [
      "Writing\nWhat urge comes up for me today? (to peek/check/search/message)",
      "What am I really trying to soothe through this urge? (uncertainty? pain? fear of abandonment? humiliation?)",
      "90-second protocol (from today onward)\n\n1. 3 slow breaths\n\n\n2. Notice the body: where the urge sits\n\n\n3. One sentence inside: “This is an urge. It will pass.”\nThen choose one small alternative.\n\n\n\nWhat one alternative do I choose today instead of checking?"
    ]
    ),
    closing: localizedGenderNode(
      [
      "Closing",
      "I don’t have to act to calm down. I can calm down without action too."
    ],
      [
      "Closing",
      "I don’t have to act to calm down. I can calm down without action too."
    ]
    ),
    action: localizedGenderNode(
      "Every time an urge comes up — I do the 90-second protocol once, only once.",
      "Every time an urge comes up — I do the 90-second protocol once, only once."
    ),
  },
  {
    dayNumber: 4,
    emotionalStage: "pattern",
    title: "Comparison (restoring self-worth)",
    opening: localizedGenderNode(
      [
      "Today I stop measuring myself against someone else.",
      "I pause with myself."
    ],
      [
      "Today I stop measuring myself against someone else.",
      "I pause with myself."
    ]
    ),
    mindfulness: localizedGenderNode(
      [
      "One breath. “Comparison is pain disguised as truth.”"
    ],
      [
      "One breath. “Comparison is pain disguised as truth.”"
    ]
    ),
    prompts: localizedGenderNode(
      [
      "Writing\nWhat is the thing I’m comparing? (beauty/age/money/ease/confidence/status)",
      "What do I tell myself it means about me?",
      "What other truth about me exists, even if I can’t feel it right now? (one sentence)",
      "What one strength/value in me does not depend on anyone?"
    ],
      [
      "Writing\nWhat is the thing I’m comparing? (beauty/age/money/ease/confidence/status)",
      "What do I tell myself it means about me?",
      "What other truth about me exists, even if I can’t feel it right now? (one sentence)",
      "What one strength/value in me does not depend on anyone?"
    ]
    ),
    closing: localizedGenderNode(
      [
      "Closing",
      "My worth doesn’t disappear because someone else appeared."
    ],
      [
      "Closing",
      "My worth doesn’t disappear because someone else appeared."
    ]
    ),
    action: localizedGenderNode(
      "I write 3 qualities/values that are mine only — and I keep them in my journal.",
      "I write 3 qualities/values that are mine only — and I keep them in my journal."
    ),
  },
  {
    dayNumber: 5,
    emotionalStage: "pattern",
    title: "Shame (the feeling of “being replaced”)",
    opening: localizedGenderNode(
      [
      "Today I touch shame without letting it break me.",
      "I pause with myself."
    ],
      [
      "Today I touch shame without letting it break me.",
      "I pause with myself."
    ]
    ),
    mindfulness: localizedGenderNode(
      [
      "One breath. “Shame wants me to hide. Today I stay.”"
    ],
      [
      "One breath. “Shame wants me to hide. Today I stay.”"
    ]
    ),
    prompts: localizedGenderNode(
      [
      "Writing\nWhat is the hardest sentence to hear inside my head? (“I wasn’t enough…”, “I was replaced…”)",
      "If a friend I love said this about themselves — what would I say to them?",
      "What is one soft sentence I’m willing to say to myself today instead of shame?",
      "What do I need right now to feel a little protected?"
    ],
      [
      "Writing\nWhat is the hardest sentence to hear inside my head? (“I wasn’t enough…”, “I was replaced…”)",
      "If a friend I love said this about themselves — what would I say to them?",
      "What is one soft sentence I’m willing to say to myself today instead of shame?",
      "What do I need right now to feel a little protected?"
    ]
    ),
    closing: localizedGenderNode(
      [
      "Closing",
      "Shame is not truth. It’s a sign that I’m hurting."
    ],
      [
      "Closing",
      "Shame is not truth. It’s a sign that I’m hurting."
    ]
    ),
    action: localizedGenderNode(
      "When shame rises — hand on chest/stomach (if it feels okay) + one soft sentence only.",
      "When shame rises — hand on chest/stomach (if it feels okay) + one soft sentence only."
    ),
  },
  {
    dayNumber: 6,
    emotionalStage: "fear",
    title: "Jealousy as relationship pain (what I’m missing right now)",
    opening: localizedGenderNode(
      [
      "Today I understand jealousy points to a need.",
      "I pause with myself."
    ],
      [
      "Today I understand jealousy points to a need.",
      "I pause with myself."
    ]
    ),
    mindfulness: localizedGenderNode(
      [
      "One breath. “Jealousy is an arrow pointing to an empty place.”"
    ],
      [
      "One breath. “Jealousy is an arrow pointing to an empty place.”"
    ]
    ),
    prompts: localizedGenderNode(
      [
      "Writing\nWhat am I truly missing right now? (warmth? belonging? safety? validation? touch? future?)",
      "How would I like to receive this — in a clean version that doesn’t harm me?",
      "Who/what can give me part of it today? (safe person/action/routine)",
      "What one small step do I take today to fill one need?"
    ],
      [
      "Writing\nWhat am I truly missing right now? (warmth? belonging? safety? validation? touch? future?)",
      "How would I like to receive this — in a clean version that doesn’t harm me?",
      "Who/what can give me part of it today? (safe person/action/routine)",
      "What one small step do I take today to fill one need?"
    ]
    ),
    closing: localizedGenderNode(
      [
      "Closing",
      "Jealousy doesn’t have to lead to action toward my ex. It can lead back to me."
    ],
      [
      "Closing",
      "Jealousy doesn’t have to lead to action toward my ex. It can lead back to me."
    ]
    ),
    action: localizedGenderNode(
      "I choose one “clean connection”: a short call with a safe person / a walk / quiet music.",
      "I choose one “clean connection”: a short call with a safe person / a walk / quiet music."
    ),
  },
  {
    dayNumber: 7,
    emotionalStage: "need",
    title: "Clean exposure (gentle ACT) — holding an image without escaping",
    opening: localizedGenderNode(
      [
      "Today I practice holding a trigger for a moment, without checking and without getting pulled under.",
      "I pause with myself."
    ],
      [
      "Today I practice holding a trigger for a moment, without checking and without getting pulled under.",
      "I pause with myself."
    ]
    ),
    mindfulness: localizedGenderNode(
      [
      "One breath. “I can be with this for one minute.”"
    ],
      [
      "One breath. “I can be with this for one minute.”"
    ]
    ),
    prompts: localizedGenderNode(
      [
      "Writing\nWhat trigger activates me the most? (photo/place/social media/song/scene)",
      "I choose a clean exposure practice:\nOne minute — I let the image be in my mind, without action. Only breathing.\n\nWhat happened after the minute? (in body/thoughts)",
      "What did I learn about the wave? (does it really “not pass”?)"
    ],
      [
      "Writing\nWhat trigger activates me the most? (photo/place/social media/song/scene)",
      "I choose a clean exposure practice:\nOne minute — I let the image be in my mind, without action. Only breathing.\n\nWhat happened after the minute? (in body/thoughts)",
      "What did I learn about the wave? (does it really “not pass”?)"
    ]
    ),
    closing: localizedGenderNode(
      [
      "Closing",
      "I grow stronger not because I ran — but because I stayed."
    ],
      [
      "Closing",
      "I grow stronger not because I ran — but because I stayed."
    ]
    ),
    action: localizedGenderNode(
      "One minute only. Not more. Consistency > force.",
      "One minute only. Not more. Consistency > force."
    ),
  },
  {
    dayNumber: 8,
    emotionalStage: "softening",
    title: "Soft boundaries (protecting the heart)",
    opening: localizedGenderNode(
      [
      "Today I set one small boundary that protects me, without a fight.",
      "I pause with myself."
    ],
      [
      "Today I set one small boundary that protects me, without a fight.",
      "I pause with myself."
    ]
    ),
    mindfulness: localizedGenderNode(
      [
      "One breath. “A boundary is protection, not punishment.”"
    ],
      [
      "One breath. “A boundary is protection, not punishment.”"
    ]
    ),
    prompts: localizedGenderNode(
      [
      "Writing\nWhat one boundary will help me this week? (not checking socials / not asking friends / not searching for info)",
      "What is my fear if I set this boundary?",
      "What is one soft answer to that fear?",
      "How do I phrase the boundary in first person? (“This week I will not _ to protect me”)"
    ],
      [
      "Writing\nWhat one boundary will help me this week? (not checking socials / not asking friends / not searching for info)",
      "What is my fear if I set this boundary?",
      "What is one soft answer to that fear?",
      "How do I phrase the boundary in first person? (“This week I will not _ to protect me”)"
    ]
    ),
    closing: localizedGenderNode(
      [
      "Closing",
      "A boundary protects a wounded heart. It’s an act of self-respect."
    ],
      [
      "Closing",
      "A boundary protects a wounded heart. It’s an act of self-respect."
    ]
    ),
    action: localizedGenderNode(
      "I activate one action that makes the boundary easier: hide/mute/remove one trigger.",
      "I activate one action that makes the boundary easier: hide/mute/remove one trigger."
    ),
  },
  {
    dayNumber: 9,
    emotionalStage: "softening",
    title: "“They’re happy and I was left behind” (breaking the myth)",
    opening: localizedGenderNode(
      [
      "Today I stop reading someone else’s life from one picture.",
      "I pause with myself."
    ],
      [
      "Today I stop reading someone else’s life from one picture.",
      "I pause with myself."
    ]
    ),
    mindfulness: localizedGenderNode(
      [
      "One breath. “I don’t know their whole story.”"
    ],
      [
      "One breath. “I don’t know their whole story.”"
    ]
    ),
    prompts: localizedGenderNode(
      [
      "Writing\nWhat automatic sentence comes up? (“They’re happy,” “I was thrown away,” “I’m less”)",
      "What do I actually know, and what am I only guessing?",
      "What is the thing I can build for myself, even if it hurts?",
      "What one truth about my path am I willing to remember today?"
    ],
      [
      "Writing\nWhat automatic sentence comes up? (“They’re happy,” “I was thrown away,” “I’m less”)",
      "What do I actually know, and what am I only guessing?",
      "What is the thing I can build for myself, even if it hurts?",
      "What one truth about my path am I willing to remember today?"
    ]
    ),
    closing: localizedGenderNode(
      [
      "Closing",
      "Their picture doesn’t define my worth — or my future."
    ],
      [
      "Closing",
      "Their picture doesn’t define my worth — or my future."
    ]
    ),
    action: localizedGenderNode(
      "Once today — I replace the automatic sentence with one true sentence (what I wrote).",
      "Once today — I replace the automatic sentence with one true sentence (what I wrote)."
    ),
  },
  {
    dayNumber: 10,
    emotionalStage: "perspective",
    title: "Anger (releasing it without destroying)",
    opening: localizedGenderNode(
      [
      "Today I let anger out in a way that respects me.",
      "I pause with myself."
    ],
      [
      "Today I let anger out in a way that respects me.",
      "I pause with myself."
    ]
    ),
    mindfulness: localizedGenderNode(
      [
      "One breath. “I’m allowed to be angry. I’m not allowed to harm myself.”"
    ],
      [
      "One breath. “I’m allowed to be angry. I’m not allowed to harm myself.”"
    ]
    ),
    prompts: localizedGenderNode(
      [
      "Writing\nWhat is my anger really about?",
      "What boundary or value is this anger trying to restore? (respect/truth/loyalty/safety)",
      "How do I release anger without checking/attacking/humiliating myself? (physical action/writing)",
      "What is one self-respecting action I take today from anger — not against myself?"
    ],
      [
      "Writing\nWhat is my anger really about?",
      "What boundary or value is this anger trying to restore? (respect/truth/loyalty/safety)",
      "How do I release anger without checking/attacking/humiliating myself? (physical action/writing)",
      "What is one self-respecting action I take today from anger — not against myself?"
    ]
    ),
    closing: localizedGenderNode(
      [
      "Closing",
      "Anger can become a force that organizes me, not a force that breaks me."
    ],
      [
      "Closing",
      "Anger can become a force that organizes me, not a force that breaks me."
    ]
    ),
    action: localizedGenderNode(
      "10 minutes of movement (fast walk/light workout) — to move energy out of the body.",
      "10 minutes of movement (fast walk/light workout) — to move energy out of the body."
    ),
  },
  {
    dayNumber: 11,
    emotionalStage: "strength",
    title: "Restoring control (three things that are in my hands)",
    opening: localizedGenderNode(
      [
      "Today I shrink chaos into one small step.",
      "I pause with myself."
    ],
      [
      "Today I shrink chaos into one small step.",
      "I pause with myself."
    ]
    ),
    mindfulness: localizedGenderNode(
      [
      "One breath. “One step is enough.”"
    ],
      [
      "One breath. “One step is enough.”"
    ]
    ),
    prompts: localizedGenderNode(
      [
      "Writing\nWhat overwhelms me the most today about this?",
      "What 3 things are in my control today? (very small)",
      "What one step only will I do today?",
      "What one thing will I release today, even a little?"
    ],
      [
      "Writing\nWhat overwhelms me the most today about this?",
      "What 3 things are in my control today? (very small)",
      "What one step only will I do today?",
      "What one thing will I release today, even a little?"
    ]
    ),
    closing: localizedGenderNode(
      [
      "Closing"
    ],
      [
      "Closing"
    ]
    ),
    action: localizedGenderNode(
      "Small action for today",
      "Small action for today"
    ),
  },
  {
    dayNumber: 12,
    emotionalStage: "release",
    title: "Night and loops (end of chapter)",
    opening: localizedGenderNode(
      [
      "Today I give my mind a “day closing” so I can rest.",
      "I pause with myself."
    ],
      [
      "Today I give my mind a “day closing” so I can rest.",
      "I pause with myself."
    ]
    ),
    mindfulness: localizedGenderNode(
      [
      "One breath. “Tonight I don’t have to solve it.”"
    ],
      [
      "One breath. “Tonight I don’t have to solve it.”"
    ]
    ),
    prompts: localizedGenderNode(
      [
      "Writing\nWhat is my most common night loop?",
      "What is the loop trying to achieve for me? (control/answer/hope)",
      "What closing sentence for the night do I choose? (one sentence)",
      "What 3-minute ritual will help me before sleep?"
    ],
      [
      "Writing\nWhat is my most common night loop?",
      "What is the loop trying to achieve for me? (control/answer/hope)",
      "What closing sentence for the night do I choose? (one sentence)",
      "What 3-minute ritual will help me before sleep?"
    ]
    ),
    closing: localizedGenderNode(
      [
      "Closing",
      "A tired mind tells strong stories. Tonight I choose rest."
    ],
      [
      "Closing",
      "A tired mind tells strong stories. Tonight I choose rest."
    ]
    ),
    action: localizedGenderNode(
      "Before sleep: my closing sentence + one breath. That’s it.",
      "Before sleep: my closing sentence + one breath. That’s it."
    ),
  },
  {
    dayNumber: 13,
    emotionalStage: "choice",
    title: "Turning jealousy into direction (what I build now)",
    opening: localizedGenderNode(
      [
      "Today jealousy turns from burning — into a compass.",
      "I pause with myself."
    ],
      [
      "Today jealousy turns from burning — into a compass.",
      "I pause with myself."
    ]
    ),
    mindfulness: localizedGenderNode(
      [
      "One breath. “What I feel tells me what matters to me.”"
    ],
      [
      "One breath. “What I feel tells me what matters to me.”"
    ]
    ),
    prompts: localizedGenderNode(
      [
      "Writing\nWhat does jealousy reveal that I want in my life? (relationship? safety? love? home?)",
      "What one value do I choose to lead me now? (respect/truth/quiet/stability)",
      "What small habit brings me closer to this value this week?",
      "What do I promise myself when a strong trigger rises? (one sentence)"
    ],
      [
      "Writing\nWhat does jealousy reveal that I want in my life? (relationship? safety? love? home?)",
      "What one value do I choose to lead me now? (respect/truth/quiet/stability)",
      "What small habit brings me closer to this value this week?",
      "What do I promise myself when a strong trigger rises? (one sentence)"
    ]
    ),
    closing: localizedGenderNode(
      [
      "Closing",
      "I don’t have to stay in comparison. I can build a life."
    ],
      [
      "Closing",
      "I don’t have to stay in comparison. I can build a life."
    ]
    ),
    action: localizedGenderNode(
      "I plan one small thing for the coming week that gives me a future back (meeting/routine/project).",
      "I plan one small thing for the coming week that gives me a future back (meeting/routine/project)."
    ),
  },
  {
    dayNumber: 14,
    emotionalStage: "continuation",
    title: "Release ritual and closing (staying with myself)",
    opening: localizedGenderNode(
      [
      "Today I close a chapter without erasing it. I return myself to myself.",
      "I pause with myself."
    ],
      [
      "Today I close a chapter without erasing it. I return myself to myself.",
      "I pause with myself."
    ]
    ),
    mindfulness: localizedGenderNode(
      [
      "One breath. “I release without disappearing.”"
    ],
      [
      "One breath. “I release without disappearing.”"
    ]
    ),
    prompts: localizedGenderNode(
      [
      "Writing\nThree things I stop doing to protect my heart:",
      "Three things I start doing to build myself:",
      "A short letter that is not sent (5 lines) — what I want to say, and then release:",
      "Return sentence for hard days (a short, clear sentence):"
    ],
      [
      "Writing\nThree things I stop doing to protect my heart:",
      "Three things I start doing to build myself:",
      "A short letter that is not sent (5 lines) — what I want to say, and then release:",
      "Return sentence for hard days (a short, clear sentence):"
    ]
    ),
    closing: localizedGenderNode(
      [
      "Closing",
      "I don’t erase emotion.",
      "I stop letting it run my life.",
      "I keep walking —",
      "slowly, quietly, with myself.",
      "And that is strength."
    ],
      [
      "Closing",
      "I don’t erase emotion.",
      "I stop letting it run my life.",
      "I keep walking —",
      "slowly, quietly, with myself.",
      "And that is strength."
    ]
    ),
    action: localizedGenderNode(
      "I choose one action that symbolizes “a new chapter”: a small home reset / a walk somewhere new / organizing my weekly journal.",
      "I choose one action that symbolizes “a new chapter”: a small home reset / a walk somewhere new / organizing my weekly journal."
    ),
  },
].map(buildNormalizedDay);

export const jealousyExMovesOnBlueprintEn = buildNormalizedPilotBlueprint({
  id: "jealousy_ex_moves_on_14d",
  version: "tri_locale_emit_en_v1",
  durationDays: 14,
  problemKey: "jealousy_ex_moves_on",
  category: "general",
  journeyName: {
    he: "קנאה כשהאקס/ית ממשיכים הלאה",
    en: "Jealousy When an Ex Moves On",
    es: "Celos cuando el/la ex sigue adelante",
  },
  days,
});

export default jealousyExMovesOnBlueprintEn;
